'use client'

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Edit, RefreshCcw } from 'lucide-react'
import ReactTimeAgo from 'react-time-ago'

import { Avatar, AvatarFallback, AvatarImage, Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { schoolGradeLabel } from '@helper/utils/domain'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { ChildProfileOptions } from '../index'
import { PremiumPill } from '../../../components'
import { useSharedTRPC } from '../../../context'
import { SkillRadar } from './skill-radar'

interface HeaderProps {
  childId: string
  className?: string
  options: ChildProfileOptions
  /** Opens the edit modal owned by ChildProfile. The button only renders when the consumer supplies this — pass it (via `ChildProfile`'s `renderEditModal`) for every viewer allowed to open the dialog, child included; `resolveChildEditContext` on the server decides which fields that viewer can actually touch. */
  onEditClick?: () => void
  /** Render prop for the back link. Header supplies the styled back icon; the consumer wraps it in its router-specific `<Link>`. Omit to hide the back link. */
  renderBackLink?: (content: ReactNode) => ReactNode
}

export function Header({ childId, className, options, onEditClick, renderBackLink }: HeaderProps) {
  const trpc = useSharedTRPC()

  const getOneChildQuery = useQuery(trpc.myOrganization.getChildDetails.queryOptions({ userId: childId }))

  return (
    <div className={cn(className)} style={{ backgroundImage: 'var(--gradient-tertiary-2)' }}>
      <section className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full srd:px-2'>
        <section className='srd:flex srd:gap-x-2 srd:flex-nowrap srd:items-center srd:text-neutral-white'>
          {renderBackLink ? (
            <div className='srd:w-fit'>{renderBackLink(<ArrowLeft className='srd:size-5' />)}</div>
          ) : null}

          <h1 className={cn(typographyVariants({ variant: 'title-md' }))}>
            Profil{' '}
            {options.type === 'web' && options.typeOfUser === 'child'
              ? 'Saya'
              : options.type === 'web' && options.typeOfUser === 'main'
                ? 'Anak'
                : 'Murid'}
          </h1>
        </section>

        <section className='srd:flex srd:flex-col srd:gap-6 srd:rounded-2xl srd:p-6 srd:text-neutrals-white srd:lg:flex-row srd:lg:items-center srd:lg:justify-around srd:lg:gap-x-4 srd:max-w-[1200px] srd:mx-auto srd:w-full'>
          {getOneChildQuery.isError ? (
            <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:gap-y-3 srd:text-center'>
              <p className='srd:text-sm srd:font-medium srd:text-neutrals-white'>Gagal memuat profil</p>
              <Button
                variant='outline'
                size='sm'
                onClick={() => getOneChildQuery.refetch()}
                loading={getOneChildQuery.isRefetching}
                disabled={getOneChildQuery.isRefetching}
              >
                <RefreshCcw className='srd:size-4 srd:mr-2' />
                Coba lagi
              </Button>
            </div>
          ) : getOneChildQuery.isPending || !getOneChildQuery.data ? (
            <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-3 srd:lg:items-start'>
              <Skeleton className='srd:size-20 srd:rounded-full' />
              <Skeleton className='srd:h-5 srd:w-40 srd:rounded-full' />
              <Skeleton className='srd:h-3 srd:w-24 srd:rounded-full' />
            </section>
          ) : (
            <section className='srd:flex srd:flex-col srd:items-center srd:gap-y-4 srd:text-center srd:lg:text-left'>
              <Avatar profile='user' className='srd:size-24'>
                <AvatarImage src={getOneChildQuery.data.image ?? undefined} />
                <AvatarFallback>{getInitials(getOneChildQuery.data.name)}</AvatarFallback>
              </Avatar>

              <section className='srd:flex srd:flex-col srd:items-center srd:text-center'>
                <p className={cn(typographyVariants({ variant: 'headline-sm' }))}>{getOneChildQuery.data.name}</p>

                <p className={cn(typographyVariants({ variant: 'body-md' }))}>
                  {schoolGradeLabel(getOneChildQuery.data.schoolClass) ?? 'Belum atur kelas'}
                </p>
              </section>

              {getOneChildQuery.data.lastActive ? (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  Terakhir aktif{' '}
                  <ReactTimeAgo
                    className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}
                    date={new Date(getOneChildQuery.data.lastActive)}
                  />
                </p>
              ) : (
                <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                  Belum pernah aktif
                </p>
              )}

              <PremiumPill isPremium={getOneChildQuery.data.isPremium} />

              {onEditClick && (
                <Button variant='ghost' size='sm' onClick={onEditClick} className='srd:text-neutrals-white'>
                  <Edit className='srd:size-4 srd:mr-2' />
                  Edit
                </Button>
              )}
            </section>
          )}

          <SkillRadar childId={childId} className='srd:lg:max-w-[380px]' />
        </section>
      </section>
    </div>
  )
}
